import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PenLine, RefreshCw, CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import api from "@/lib/api";

const CHANNEL = { "public-qr": "Link/QR", "in-app": "App", manual: "Manuell" };
const deDate = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }) : "—");

function AlertState({ a }) {
  if (!a || (!a.done && !a.attempts)) return <span className="text-amber-300 inline-flex items-center gap-1"><Clock size={13} /> wartet</span>;
  if (a.preview_skip || a.baseline_skip) return <span className="text-zinc-400">vor Einführung</span>;
  if (a.done && !a.failed) return <span className="text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 size={13} /> gemeldet</span>;
  return <span className="text-red-400 inline-flex items-center gap-1" title={a.last_error || ""}><AlertTriangle size={13} /> {a.done ? "fehlgeschlagen" : `Versuch ${a.attempts}`}</span>;
}

export function SignatureAlertsPanel() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/signature-alerts").then((r) => setItems(r.data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const resend = async (id) => {
    setBusy(id);
    try {
      const r = await api.post(`/admin/signature-alerts/${id}/resend`);
      const s = r.data.signed_alert || {};
      if (s.failed) toast.error(`Teilweise fehlgeschlagen: ${s.last_error || ""}`);
      else toast.success(`Meldung gesendet an ${(s.emailed || []).length} Empfänger`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Senden fehlgeschlagen");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section data-testid="signature-alerts-panel" className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4">
      <div className="flex items-center gap-2 mb-3">
        <PenLine size={16} className="text-[#00E575]" />
        <h2 className="font-display font-semibold text-zinc-100">Unterschrifts-Meldungen (30 Tage)</h2>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-zinc-400">Keine Unterschriften in den letzten 30 Tagen.</p>
      ) : (
        <ul className="divide-y divide-zinc-800">
          {items.map((o) => (
            <li key={o.id} data-testid={`sig-alert-row-${o.id}`} className="py-2.5 flex items-center gap-3 text-sm">
              <div className="min-w-0 flex-1">
                <Link to={`/projects/${o.project_id}`} className="text-zinc-100 font-medium hover:underline truncate block">
                  {o.offer_number} · {o.signature?.signer_name || "—"}
                </Link>
                <div className="text-xs text-zinc-400">
                  <span>{deDate(o.signature?.signed_at)}</span>
                  <span> · {CHANNEL[o.channel] || "App"} · </span>
                  <AlertState a={o.signed_alert} />
                </div>
              </div>
              <button
                data-testid={`sig-alert-resend-${o.id}`}
                onClick={() => resend(o.id)}
                disabled={busy === o.id}
                className="h-9 px-3 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 bg-zinc-800 border border-zinc-700 text-zinc-200 hover:border-zinc-500 disabled:opacity-40 shrink-0"
              >
                <RefreshCw size={13} className={busy === o.id ? "animate-spin" : ""} /> Erneut melden
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
